/**
 * 接口异常采集
 *
 * 只报故障，不报业务：4xx（401 未登录 / 403 无权限 / 404 / 422 参数校验）
 * 和业务 code != 0 都属于产品逻辑，报了全是噪音。
 * 真正要报的只有 5xx、网络错误，以及成功但过慢的接口；请求超时不再上报。
 */
import { MONITOR_CONFIG, SENSITIVE_KEY_PATTERN, SLOW_API_OVERRIDES } from '@/monitor/config';
import { track } from '@/monitor/core/aggregator';
import { describeRequestTiming, setupRequestTimingCollector } from '@/monitor/core/requestTiming';
import type {
  ApiErrorContext,
  ApiErrorPayload,
  NavigatorWithConnection,
  TimedRequestConfig,
} from '@/types/monitor';

/** 把 URL 里的动态段替换成占位符，让 /session/123 与 /session/456 收敛为同一指纹 */
const normalizeUrl = (url: string): string =>
  (url.split('?')[0] ?? url)
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '/{uuid}')
    .replace(/\/\d+/g, '/{id}');

/** Error 上挂接口上下文的字段名，加前缀避免与业务字段冲突 */
const API_CONTEXT_KEY = '__monitorApiContext';

/**
 * 递归脱敏：命中 SENSITIVE_KEY_PATTERN 的字段值替换为 ***。
 * 告警会发进钉钉群，参数原样带出去等于把凭证和用户数据发给群里所有人。
 */
const redact = (value: unknown, depth = 0): unknown => {
  if (depth > 4 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((item) => redact(item, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    out[key] = SENSITIVE_KEY_PATTERN.test(key) ? '***' : redact(val, depth + 1);
  }
  return out;
};

/**
 * 把请求参数序列化成可读且安全的短字符串。
 *
 * 注意：axios 在发送前已把请求体序列化成 JSON 字符串，`config.data` 拿到的通常是
 * **字符串**而非对象。若直接原样返回，脱敏会被整个跳过，密码和 token 就会明文进群。
 * 因此字符串要先尝试 parse 回对象再脱敏。
 */
export const serializePayload = (payload: unknown): string | undefined => {
  if (payload === undefined || payload === null || payload === '') return undefined;
  /** 有意例外：参数可能含循环引用或 FormData，序列化失败不能影响业务 */
  try {
    if (payload instanceof FormData) {
      return `FormData(${[...payload.keys()].join(', ')})`;
    }
    let target: unknown = payload;
    if (typeof payload === 'string') {
      try {
        target = JSON.parse(payload);
      } catch {
        /** 不是 JSON（如 form-urlencoded 串），无法结构化脱敏，整体丢弃更安全 */
        return '(非 JSON 参数，已省略)';
      }
    }
    const raw = JSON.stringify(redact(target));
    if (!raw) return undefined;
    return raw.length > MONITOR_CONFIG.MAX_PAYLOAD_LENGTH
      ? raw.slice(0, MONITOR_CONFIG.MAX_PAYLOAD_LENGTH) + '…(已截断)'
      : raw;
  } catch {
    return '(参数无法序列化)';
  }
};

/**
 * 在 reject 前把接口上下文挂到 Error 上。
 * 由 request.ts 的拦截器调用，供 vueError / jsError 采集器还原错误来源。
 */
export const attachApiContext = (err: unknown, context: ApiErrorContext): void => {
  /** 有意例外：挂载失败（如 err 被冻结）不能影响正常的错误传递 */
  try {
    if (err && typeof err === 'object') {
      (err as Record<string, unknown>)[API_CONTEXT_KEY] = context;
    }
  } catch {
    /** 静默降级 */
  }
};

/** 读取 Error 上的接口上下文；不是接口错误则返回 undefined */
export const readApiContext = (err: unknown): ApiErrorContext | undefined => {
  if (!err || typeof err !== 'object') return undefined;
  const ctx = (err as Record<string, unknown>)[API_CONTEXT_KEY];
  return ctx && typeof ctx === 'object' ? (ctx as ApiErrorContext) : undefined;
};

/** Axios 请求超时：用于在请求失败和未捕获异常两条链路中统一过滤。 */
export const isRequestTimeout = (err: unknown): boolean => {
  const error = err as { code?: string; message?: string } | null | undefined;
  return (
    error?.code === 'ECONNABORTED' ||
    error?.code === 'ETIMEDOUT' ||
    /timeout/i.test(error?.message ?? '')
  );
};

/** 请求拦截器调用：打起始时间戳；performance.now() 用于和浏览器计时对齐 */
export const markRequestStart = (config: TimedRequestConfig): void => {
  config.monitorStart = Date.now();
  config.monitorPerfStart = performance.now();
};

const elapsed = (config?: TimedRequestConfig): number =>
  config?.monitorStart ? Date.now() - config.monitorStart : 0;

/** 毫秒转「3小时12分」这类可读时长 */
const formatDuration = (ms: number): string => {
  const min = Math.floor(ms / 60_000);
  if (min < 1) return `${Math.floor(ms / 1000)}秒`;
  if (min < 60) return `${min}分`;
  return `${Math.floor(min / 60)}小时${min % 60}分`;
};

/**
 * 客户端环境快照。
 *
 * 接口超时既可能是后端慢，也可能是用户这边休眠 / 断网 / 弱网 / 页面挂后台，
 * 光看耗时无法区分。把这几项一并带上，收到告警时才不用靠猜。
 * 「页面已开」用 performance.now()，长期不刷新的挂机标签页会明显偏大。
 */
const clientState = (): Record<string, string> => {
  const state: Record<string, string> = {
    网络: navigator.onLine ? '在线' : '离线',
    页面状态: document.visibilityState === 'visible' ? '前台' : '后台',
    页面已开: formatDuration(performance.now()),
    ...pageLifecycleState(),
  };
  const conn = (navigator as NavigatorWithConnection).connection;
  if (conn?.effectiveType) state.网络类型 = conn.effectiveType;
  return state;
};

/** 回到前台多久以内算「刚回来」：休眠唤醒、隔夜回到电脑前时，积压的请求和推送集中在这段时间 */
const RECENT_RESUME_MS = 60_000;

/** 页面进入后台的时间（墙钟），在前台时为 0 */
let hiddenAt = document.hidden ? Date.now() : 0;
/** 最近一次回到前台的时间，以及回来之前在后台停留了多久 */
let visibleAt = 0;
let lastHiddenDuration = 0;
/** 最近一次被浏览器冻结后恢复的时间（Page Lifecycle 的 resume 事件，仅 Chromium 支持） */
let resumedAt = 0;

/**
 * 前后台上下文：区分「挂在后台」「刚从后台 / 冻结恢复」和普通前台操作。
 * 用墙钟而不是 performance.now()：系统休眠期间后者在部分平台上不走，算不出真实的后台时长。
 */
const pageLifecycleState = (): Record<string, string> => {
  const now = Date.now();
  const state: Record<string, string> = {};
  if (document.hidden) {
    if (hiddenAt) state.已在后台 = formatDuration(now - hiddenAt);
  } else if (visibleAt && now - visibleAt < RECENT_RESUME_MS && lastHiddenDuration) {
    state.刚回前台 = `${Math.round((now - visibleAt) / 1000)}秒前（此前后台 ${formatDuration(lastHiddenDuration)}）`;
  }
  if (resumedAt && now - resumedAt < RECENT_RESUME_MS) {
    state.刚从冻结恢复 = `${Math.round((now - resumedAt) / 1000)}秒前`;
  }
  return state;
};

/** 监控启动时调用：开始收集接口的浏览器计时与页面前后台切换 */
export const setupApiTimingCollector = (): void => {
  setupRequestTimingCollector();
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      hiddenAt = Date.now();
      return;
    }
    lastHiddenDuration = hiddenAt ? Date.now() - hiddenAt : 0;
    hiddenAt = 0;
    visibleAt = Date.now();
  });
  document.addEventListener('resume', () => {
    resumedAt = Date.now();
  });
};

const report = (payload: ApiErrorPayload): void => {
  const path = normalizeUrl(payload.url);
  if (payload.slow) {
    track({
      type: 'api_slow',
      level: 'warn',
      title: '接口响应缓慢',
      message: `${payload.method} ${path} 响应超过阈值`,
      source: `${payload.method} ${path}`,
      extra: {
        实测: `${payload.duration}ms`,
        阈值: `${payload.threshold ?? MONITOR_CONFIG.THRESHOLD.SLOW_API}ms`,
        ...payload.timing,
        ...clientState(),
      },
      timestamp: Date.now(),
    });
    return;
  }
  track({
    type: 'api_error',
    level: 'error',
    title: '接口异常',
    message: payload.message,
    source: `${payload.method} ${path}${payload.status ? ` → ${payload.status}` : ''}`,
    extra: {
      耗时: `${payload.duration}ms`,
      ...payload.timing,
      ...clientState(),
    },
    timestamp: Date.now(),
  });
};

/** 响应成功分支调用：耗时超阈值时报慢接口 */
export const reportSlowRequest = (config?: TimedRequestConfig): void => {
  const url = config?.url ?? '';
  /** 按接口覆盖优先，0 表示该接口不限制耗时 */
  const threshold = SLOW_API_OVERRIDES[normalizeUrl(url)] ?? MONITOR_CONFIG.THRESHOLD.SLOW_API;
  if (threshold === 0) return;
  const duration = elapsed(config);
  if (duration < threshold) return;
  report({
    method: (config?.method ?? 'get').toUpperCase(),
    url,
    duration,
    message: '响应超过阈值',
    slow: true,
    threshold,
    timing: describeRequestTiming(url, config?.monitorPerfStart),
  });
};

/** 响应失败分支调用：只上报 5xx / 网络错误，请求超时直接忽略 */
export const reportRequestFailure = (err: unknown): void => {
  const e = err as {
    code?: string;
    message?: string;
    config?: TimedRequestConfig;
    response?: { status?: number };
  };
  if (isRequestTimeout(err)) return;
  const status = e?.response?.status;
  /** 有响应且非 5xx → 属于业务或权限逻辑，不是故障 */
  if (status !== undefined && status < 500) return;
  const url = e?.config?.url ?? '';
  report({
    method: (e?.config?.method ?? 'get').toUpperCase(),
    url,
    status,
    duration: elapsed(e?.config),
    message: status ? `HTTP ${status}` : '网络错误',
    timing: describeRequestTiming(url, e?.config?.monitorPerfStart),
  });
};
